import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { isEmpty } from 'rambda';
import { friendsQueryOptions } from '@/hooks/contacts/use-friends';
import { generateFriendInfoQueryOptions } from '@/hooks/users/use-friend-info';
import type { RespondedType, ResponseError } from '@/models/response';
import { addToast, addTranslateToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';

export default function useUpdateFriendRemark(friendId: Nullable<number>) {
  const queryClient = useQueryClient();

  return useMutation<RespondedType, ResponseError, string>({
    mutationKey: ['/RemarkFriend', friendId],
    mutationFn: friendId
      ? (params) =>
          fetcher(
            '/RemarkFriend',
            {
              FriendUserID: friendId,
              RemarkName: params.trim(),
            },
            { validResponseCodes: [0] },
          )
      : undefined,
    onSuccess: (_, params) => {
      if (!friendId) return;

      queryClient.setQueryData(friendsQueryOptions.queryKey, (state) =>
        produce(state, (draft) => {
          if (!draft?.[friendId]) return;

          draft[friendId].RemarkName = params;
        }),
      );
      queryClient.setQueryData(generateFriendInfoQueryOptions(friendId).queryKey, (state) =>
        produce(state, (draft) => {
          if (!draft?.FriendInfo) return;

          draft.FriendInfo.RemarkName = params;
        }),
      );
    },
    onSettled: (data, error, params) => {
      if (isEmpty(params) && data?.Code === 0) {
        addTranslateToast({
          type: 'success',
          message: 'user.remark.reset-to-original',
        });
      } else {
        addToast({
          type: data ? 'success' : 'error',
          message: data?.Msg ?? error?.message,
        });
      }
    },
  });
}
